import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Newspaper, Plus, Send, Sparkles, Trash2, Edit, Eye, 
  Users, School, Loader2, FileText, Image, Paperclip,
  X, ChevronLeft, Mail, Calendar, CheckCircle, ChevronUp, ChevronDown,
  Download, RefreshCw, Star, Heart, BookOpen, Shield, PartyPopper,
  Bell, Camera, GraduationCap, Stethoscope, Megaphone, Upload, Copy, Share2, ClipboardCopy, LayoutTemplate,
  Clock, Cake, Palmtree, Truck, ListChecks, AlertTriangle, Sun
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import API from '@/config/api';
import PosterBuilderDialog from '@/components/PosterBuilderDialog';

const NewslettersPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewNewsletter, setPreviewNewsletter] = useState(null);
  const [generatingFull, setGeneratingFull] = useState(false);
  
  // Editor state
  const [title, setTitle] = useState('');
  const [sections, setSections] = useState([
    { id: '1', type: 'announcement', title: 'Center News & Announcements', content: '' }
  ]);
  const [audienceType, setAudienceType] = useState('center');
  const [classroomId, setClassroomId] = useState('');
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [frequency, setFrequency] = useState('monthly');
  
  // AI assist state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  // Generate dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatePeriod, setGeneratePeriod] = useState('');
  const [generateNotes, setGenerateNotes] = useState('');

  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importRefineWithAi, setImportRefineWithAi] = useState(true);
  const [importing, setImporting] = useState(false);

  // Poster newsletter builder
  const [showPosterDialog, setShowPosterDialog] = useState(false);

  const sectionTypes = [
    // ---- Poster newsletter sections (match the settings-driven single-page template) ----
    { type: 'important_reminders',    label: 'Important Reminders',     icon: AlertTriangle,  color: 'bg-blue-50 text-blue-900 border-blue-300',        accent: '#1E3A8A' },
    { type: 'center_hours',           label: 'Center Hours',            icon: Clock,          color: 'bg-blue-50 text-blue-700 border-blue-200',        accent: '#2563EB' },
    { type: 'welcome_month',          label: 'Welcome (Month)',         icon: Sun,            color: 'bg-amber-50 text-amber-800 border-amber-300',     accent: '#E5A520' },
    { type: 'happy_birthday',         label: 'Happy Birthday',          icon: Cake,           color: 'bg-pink-50 text-pink-700 border-pink-200',        accent: '#DB2777' },
    { type: 'vacation_notice',        label: 'Center Vacation Notice',  icon: Palmtree,       color: 'bg-red-50 text-red-700 border-red-200',           accent: '#DC2626' },
    { type: 'our_classrooms',         label: 'Our Classrooms',          icon: School,         color: 'bg-blue-50 text-blue-700 border-blue-200',        accent: '#1D4ED8' },
    { type: 'program_highlights',     label: 'Our Program Highlights',  icon: Heart,          color: 'bg-green-50 text-green-700 border-green-200',     accent: '#16A34A' },
    { type: 'transportation',         label: 'Transportation Reminder', icon: Truck,          color: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: '#10B981' },
    { type: 'what_to_bring',          label: 'What to Bring Daily',     icon: ListChecks,     color: 'bg-pink-50 text-pink-700 border-pink-200',        accent: '#EC4899' },
    // ---- Original classic sections (kept for backward compat) ----
    { type: 'announcement', label: 'Center News',      icon: Megaphone,      color: 'bg-blue-50 text-blue-700 border-blue-200',       accent: '#3B82F6' },
    { type: 'events',       label: 'Upcoming Events',  icon: Calendar,       color: 'bg-purple-50 text-purple-700 border-purple-200', accent: '#8B5CF6' },
    { type: 'reminders',    label: 'Reminders',        icon: Bell,           color: 'bg-amber-50 text-amber-700 border-amber-200',    accent: '#F59E0B' },
    { type: 'photos',       label: 'Staff Spotlight',  icon: Camera,         color: 'bg-pink-50 text-pink-700 border-pink-200',       accent: '#EC4899' },
    { type: 'celebrations', label: 'Celebrations',     icon: PartyPopper,    color: 'bg-green-50 text-green-700 border-green-200',    accent: '#10B981' },
    { type: 'health',       label: 'Health & Safety',  icon: Stethoscope,    color: 'bg-red-50 text-red-700 border-red-200',          accent: '#EF4444' },
    { type: 'learning',     label: 'Learning Updates', icon: GraduationCap,  color: 'bg-indigo-50 text-indigo-700 border-indigo-200', accent: '#6366F1' },
    { type: 'general',      label: 'General Update',   icon: FileText,       color: 'bg-gray-50 text-gray-700 border-gray-200',       accent: '#6B7280' },
  ];

  const getSectionType = (type) => sectionTypes.find(s => s.type === type) || sectionTypes[sectionTypes.length - 1];

  const [posterDefaults, setPosterDefaults] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);

  // Lazily load poster defaults + this-month preview so poster-style
  // "Add Section" clicks can auto-fill content.
  const loadPosterContext = async () => {
    try {
      const now = new Date();
      const [defRes, prevRes] = await Promise.all([
        posterDefaults ? Promise.resolve({ data: posterDefaults }) : axios.get(`${API}/newsletters/poster/defaults`, getAuthHeaders()),
        axios.get(`${API}/newsletters/poster/preview?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, getAuthHeaders()),
      ]);
      setPosterDefaults(defRes.data);
      setPosterPreview(prevRes.data);
      return { d: defRes.data, p: prevRes.data };
    } catch {
      return { d: null, p: null };
    }
  };

  const posterSectionContent = (type, d, p) => {
    const now = new Date();
    const monthName = now.toLocaleString('en-US', { month: 'long' });
    switch (type) {
      case 'important_reminders':
        return (d?.important_reminders || []).map(r => `• ${r}`).join('\n');
      case 'center_hours': {
        const parts = [d?.hours_line || ''].filter(Boolean);
        if (d?.closed_holidays_note) parts.push(`Closed: ${d.closed_holidays_note}`);
        return parts.join('\n');
      }
      case 'welcome_month':
        return `Welcome ${monthName}!\n\nWe hope everyone is having an amazing month. Thank you for being part of our family. `;
      case 'happy_birthday': {
        const b = p?.birthdays || [];
        if (!b.length) return `No birthdays in ${monthName} — check back next month!`;
        return `To our ${p?.center_name || 'center'} Stars — we hope you have a wonderful day!\n\n` +
          b.map(x => `🎂 ${x.name} — ${monthName} ${x.day}`).join('\n');
      }
      case 'vacation_notice':
        return 'The center will be CLOSED for vacation from __ to __. We reopen on __. Please plan childcare arrangements in advance.';
      case 'our_classrooms': {
        const rooms = p?.classrooms || [];
        if (!rooms.length) return 'Add your classrooms in Settings → Classrooms and they will show up here automatically.';
        return rooms.map(cr => `${cr.name} — ${cr.age_range}`).join('\n');
      }
      case 'program_highlights':
        return (d?.program_highlights || []).map(h => `♥ ${h}`).join('\n');
      case 'transportation':
        return d?.transportation_reminder || '';
      case 'what_to_bring':
        return (d?.what_to_bring_daily || []).map(w => `✓ ${w}`).join('\n');
      default:
        return '';
    }
  };

  const POSTER_TYPES = new Set([
    'important_reminders', 'center_hours', 'welcome_month', 'happy_birthday',
    'vacation_notice', 'our_classrooms', 'program_highlights', 'transportation', 'what_to_bring'
  ]);

  const addSection = async (type) => {
    const sectionType = getSectionType(type);
    let content = '';
    let title = sectionType.label;
    if (POSTER_TYPES.has(type)) {
      const { d, p } = await loadPosterContext();
      content = posterSectionContent(type, d, p) || '';
      if (type === 'welcome_month') {
        const now = new Date();
        title = `Welcome ${now.toLocaleString('en-US', { month: 'long' })}!`;
      }
    }
    setSections(prev => [...prev, {
      id: Date.now().toString(),
      type,
      title,
      content,
    }]);
  };

  const removeSection = (id) => {
    if (sections.length > 1) {
      setSections(sections.filter(s => s.id !== id));
    } else {
      toast.error('Newsletter must have at least one section');
    }
  };

  const updateSection = (id, field, value) => {
    setSections(sections.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < sections.length) {
      [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
      setSections(newSections);
    }
  };

  const _EMOJI_TO_TYPE = {
    '📢': 'announcement', '📅': 'events', '⏰': 'reminders', '📸': 'photos',
    '🎉': 'celebrations', '🏥': 'health', '📚': 'learning', '📝': 'general',
    // Poster newsletter section emoji ↔ type mapping (kept consistent so round-trip works)
    '⚠️': 'important_reminders', '🕒': 'center_hours', '☀️': 'welcome_month',
    '🎂': 'happy_birthday', '🌴': 'vacation_notice', '🏫': 'our_classrooms',
    '💚': 'program_highlights', '🚌': 'transportation', '✅': 'what_to_bring',
  };
  const _TYPE_TO_EMOJI = Object.fromEntries(Object.entries(_EMOJI_TO_TYPE).map(([e, t]) => [t, e]));

  const sectionsToContent = () => {
    return sections.map(s => {
      return `${_TYPE_TO_EMOJI[s.type] || '📝'} ${s.title}\n\n${s.content}`;
    }).join('\n\n---\n\n');
  };

  const contentToSections = (content) => {
    if (!content) return [{ id: '1', type: 'announcement', title: 'Center News & Announcements', content: '' }];

    const parts = content.split('\n\n---\n\n');
    return parts.map((part, index) => {
      const lines = part.split('\n\n');
      const headerLine = lines[0] || '';
      const contentPart = lines.slice(1).join('\n\n');

      let detectedType = 'general';
      for (const [emoji, type] of Object.entries(_EMOJI_TO_TYPE)) {
        if (headerLine.includes(emoji)) {
          detectedType = type;
          break;
        }
      }
      
      const sectionType = getSectionType(detectedType);
      const cleanTitle = headerLine.replace(/[📢📅⏰📸🎉🏥📚📝⚠️🕒☀️🎂🌴🏫💚🚌✅]/g, '').trim() || sectionType.label;
      
      return {
        id: (index + 1).toString(),
        type: detectedType,
        title: cleanTitle,
        content: contentPart
      };
    });
  };

  useEffect(() => {
    fetchNewsletters();
    fetchClassrooms();
    // Set default period
    const now = new Date();
    setGeneratePeriod(now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
  }, []);

  const fetchNewsletters = async () => {
    try {
      const res = await axios.get(`${API}/newsletters`, getAuthHeaders());
      setNewsletters(res.data);
    } catch (error) {
      console.error('Failed to fetch newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const res = await axios.get(`${API}/classrooms`, getAuthHeaders());
      setClassrooms(res.data);
    } catch (error) {
      console.error('Failed to fetch classrooms:', error);
    }
  };

  const resetEditor = () => {
    setTitle('');
    setSections([{ id: '1', type: 'announcement', title: 'Center News & Announcements', content: '' }]);
    setAudienceType('center');
    setClassroomId('');
    setImages([]);
    setAttachments([]);
    setEditingNewsletter(null);
    setAiPrompt('');
    setActiveSectionId(null);
    setFrequency('monthly');
  };

  const openEditor = (newsletter = null) => {
    if (newsletter) {
      setEditingNewsletter(newsletter);
      setTitle(newsletter.title);
      setSections(contentToSections(newsletter.content));
      setAudienceType(newsletter.audience_type);
      setClassroomId(newsletter.classroom_id || '');
      setImages(newsletter.images || []);
      setAttachments(newsletter.attachments || []);
    } else {
      resetEditor();
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    resetEditor();
  };

  // Import existing newsletter file (PDF / DOCX / TXT)
  const handleImportNewsletter = async () => {
    if (!importFile) {
      toast.error('Please select a file to upload');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('refine_with_ai', importRefineWithAi ? 'true' : 'false');

      const res = await axios.post(`${API}/newsletters/import-file`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });

      const data = res.data || {};
      setEditingNewsletter(null);
      setTitle(data.title || 'Imported Newsletter');
      setAudienceType('center');
      setClassroomId('');
      setImages([]);
      setAttachments([]);

      if (Array.isArray(data.sections) && data.sections.length > 0) {
        setSections(
          data.sections.map((s, i) => ({
            id: (i + 1).toString(),
            type: s.type || 'general',
            title: s.title || getSectionType(s.type || 'general').label,
            content: s.content || '',
          }))
        );
      } else {
        setSections([{ id: '1', type: 'general', title: 'Imported Content', content: '' }]);
      }

      setShowImportDialog(false);
      setImportFile(null);
      setShowEditor(true);

      if (data.refined) {
        toast.success('Newsletter imported and refined with AI. Review before publishing.');
      } else if (data.warning) {
        toast.warning(data.warning);
      } else {
        toast.success('Newsletter imported. Edit the content or use AI to polish each section.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import newsletter');
    } finally {
      setImporting(false);
    }
  };

  // Duplicate an existing newsletter into a fresh editable draft
  const handleDuplicateNewsletter = (newsletter) => {
    setEditingNewsletter(null); // treat as new — creates a fresh draft on save
    setTitle(`${newsletter.title} (Copy)`);
    setSections(contentToSections(newsletter.content));
    setAudienceType(newsletter.audience_type || 'center');
    setClassroomId(newsletter.classroom_id || '');
    setImages(newsletter.images || []);
    setAttachments(newsletter.attachments || []);
    setAiPrompt('');
    setActiveSectionId(null);
    setShowEditor(true);
    toast.success('Newsletter duplicated. Update the details and save as a new draft.');
  };

  // AI Generate Full Newsletter
  const handleGenerateFullNewsletter = async () => {
    setGeneratingFull(true);
    setShowGenerateDialog(false);
    try {
      const res = await axios.post(`${API}/newsletters/generate-full`, {
        period: generatePeriod,
        center_name: 'CareKids WI',
        special_notes: generateNotes
      }, getAuthHeaders());

      const data = res.data;
      setTitle(data.title || `${generatePeriod} Newsletter`);
      if (data.sections && Array.isArray(data.sections)) {
        setSections(data.sections.map((s, i) => ({
          id: (i + 1).toString(),
          type: s.type || 'general',
          title: s.title || getSectionType(s.type).label,
          content: s.content || ''
        })));
      }
      setShowEditor(true);
      toast.success('Newsletter generated! Review and edit the content.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate newsletter');
    } finally {
      setGeneratingFull(false);
    }
  };

  const saveNewsletter = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    const hasContent = sections.some(s => s.content.trim());
    if (!hasContent) {
      toast.error('Please add content to at least one section');
      return;
    }
    if (audienceType === 'classroom' && !classroomId) {
      toast.error('Please select a classroom');
      return;
    }

    setSaving(true);
    try {
      const data = {
        title,
        content: sectionsToContent(),
        audience_type: audienceType,
        classroom_id: audienceType === 'classroom' ? classroomId : null,
        images,
        attachments
      };

      if (editingNewsletter) {
        await axios.put(`${API}/newsletters/${editingNewsletter.id}`, data, getAuthHeaders());
        toast.success('Newsletter updated');
      } else {
        await axios.post(`${API}/newsletters`, data, getAuthHeaders());
        toast.success('Newsletter saved as draft');
      }
      fetchNewsletters();
      closeEditor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save newsletter');
    } finally {
      setSaving(false);
    }
  };

  const publishNewsletter = async (newsletterId) => {
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/newsletters/${newsletterId}/publish`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchNewsletters();
      closeEditor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const deleteNewsletter = async (newsletterId) => {
    if (!window.confirm('Delete this newsletter?')) return;
    try {
      await axios.delete(`${API}/newsletters/${newsletterId}`, getAuthHeaders());
      toast.success('Newsletter deleted');
      fetchNewsletters();
    } catch (error) {
      toast.error('Failed to delete newsletter');
    }
  };

  const handleExportPDF = async (newsletterId) => {
    try {
      const response = await axios({
        method: 'GET',
        url: `${API}/newsletters/${newsletterId}/export-pdf`,
        headers: getAuthHeaders().headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `newsletter.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleCopyShareCaption = async (newsletter) => {
    try {
      const res = await axios.post(
        `${API}/newsletters/${newsletter.id}/share-caption`,
        {},
        getAuthHeaders()
      );
      const caption = res.data?.caption || '';
      if (!caption) {
        toast.error('No caption generated');
        return;
      }
      try {
        await navigator.clipboard.writeText(caption);
        toast.success(
          res.data?.source === 'ai'
            ? 'AI caption copied — ready to paste into Facebook or Instagram!'
            : 'Caption copied — ready to paste!'
        );
      } catch {
        // Clipboard API blocked (e.g. non-HTTPS or permission). Fall back.
        window.prompt('Copy this caption:', caption);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate caption');
    }
  };

  const handleDownloadShareCard = async (newsletter) => {
    try {
      const response = await axios({
        method: 'GET',
        url: `${API}/newsletters/${newsletter.id}/share-card`,
        headers: getAuthHeaders().headers,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = (newsletter.title || 'newsletter').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_').slice(0, 40);
      link.setAttribute('download', `share_${safeTitle}.png`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Share card downloaded — ready to post to Facebook or Instagram!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate share card');
    }
  };

  const handleAiAssist = async (assistType, sectionId = null) => {
    const targetSection = sectionId ? sections.find(s => s.id === sectionId) : null;
    const currentContent = targetSection ? targetSection.content : sectionsToContent();
    
    if (!aiPrompt.trim() && assistType === 'suggest') {
      toast.error('Please enter a topic or prompt');
      return;
    }
    if (!currentContent.trim() && (assistType === 'improve' || assistType === 'expand')) {
      toast.error('Please add some content first');
      return;
    }

    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/newsletters/ai-assist`, {
        prompt: aiPrompt || 'Make this better',
        current_content: currentContent,
        assist_type: assistType
      }, getAuthHeaders());

      if (sectionId && targetSection) {
        if (assistType === 'suggest') {
          updateSection(sectionId, 'content', targetSection.content + (targetSection.content ? '\n\n' : '') + res.data.suggestion);
        } else {
          updateSection(sectionId, 'content', res.data.suggestion);
        }
      } else {
        const activeSection = activeSectionId ? sections.find(s => s.id === activeSectionId) : sections[0];
        if (activeSection) {
          if (assistType === 'suggest') {
            updateSection(activeSection.id, 'content', activeSection.content + (activeSection.content ? '\n\n' : '') + res.data.suggestion);
          } else {
            updateSection(activeSection.id, 'content', res.data.suggestion);
          }
        }
      }
      toast.success('AI suggestion applied!');
      setAiPrompt('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI assistance failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/newsletters/upload-image`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      setImages(prev => [...prev, res.data.url]);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/newsletters/upload-attachment`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      setAttachments(prev => [...prev, { name: res.data.name, url: res.data.url }]);
      toast.success('Attachment uploaded');
    } catch (error) {
      toast.error('Failed to upload attachment');
    }
  };

  // ====== BROCHURE-STYLE PREVIEW COMPONENT ======
  const BrochurePreview = ({ newsletter }) => {
    if (!newsletter) return null;
    const parsedSections = newsletter.content ? contentToSections(newsletter.content) : [];

    return (
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-2xl mx-auto" data-testid="brochure-preview">
        {/* Brochure Header */}
        <div className="relative bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 px-8 py-10">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4">
              <Star className="w-4 h-4 text-white" />
              <span className="text-white/90 text-xs font-medium tracking-wider uppercase">CareKids WI</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">{newsletter.title}</h1>
            <p className="text-white/80 text-sm">
              {newsletter.published_at
                ? new Date(newsletter.published_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                : 'Draft Preview'
              }
            </p>
          </div>
          {/* Decorative wave */}
          <svg className="absolute bottom-0 left-0 right-0 text-white" viewBox="0 0 1200 40" fill="currentColor" preserveAspectRatio="none" style={{ height: '20px' }}>
            <path d="M0,20 C300,40 600,0 900,20 C1050,30 1150,15 1200,20 L1200,40 L0,40 Z" />
          </svg>
        </div>

        {/* Sections */}
        <div className="px-6 py-6 space-y-6">
          {parsedSections.map((section, idx) => {
            const sType = getSectionType(section.type);
            const IconComp = sType.icon;
            return (
              <div key={idx} className={`rounded-xl border-2 overflow-hidden ${sType.color}`}>
                {/* Section Header */}
                <div className="flex items-center gap-3 px-5 py-3 border-b" style={{ borderColor: sType.accent + '30' }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: sType.accent + '20' }}>
                    <IconComp className="w-5 h-5" style={{ color: sType.accent }} />
                  </div>
                  <h3 className="font-bold text-base" style={{ color: sType.accent }}>{section.title}</h3>
                </div>
                {/* Section Content */}
                <div className="px-5 py-4 bg-white/60">
                  {section.content.split('\n').map((line, li) => {
                    if (!line.trim()) return <div key={li} className="h-2" />;
                    if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
                      return (
                        <div key={li} className="flex items-start gap-2 mb-1.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: sType.accent }} />
                          <p className="text-sm text-gray-700 leading-relaxed">{line.replace(/^[-•]\s*/, '')}</p>
                        </div>
                      );
                    }
                    return <p key={li} className="text-sm text-gray-700 leading-relaxed mb-2">{line}</p>;
                  })}
                </div>
              </div>
            );
          })}

          {/* Images */}
          {newsletter.images?.length > 0 && (
            <div className="rounded-xl border-2 border-pink-200 overflow-hidden bg-pink-50">
              <div className="flex items-center gap-3 px-5 py-3 border-b border-pink-200">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-pink-100">
                  <Camera className="w-5 h-5 text-pink-600" />
                </div>
                <h3 className="font-bold text-base text-pink-600">Photo Gallery</h3>
              </div>
              <div className="px-5 py-4 bg-white/60">
                <div className="grid grid-cols-2 gap-3">
                  {newsletter.images.map((img, idx) => (
                    <img key={idx} src={`${API}${img}`} alt="" className="w-full h-32 object-cover rounded-lg shadow-sm" />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-8 py-5 text-center">
          <p className="text-white font-medium text-sm">CareKids WI - Nurturing Growth, Inspiring Futures</p>
          <p className="text-white/70 text-xs mt-1">www.carekidswi.com</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ====== EDITOR VIEW ======
  if (showEditor) {
    return (
      <div className="space-y-6" data-testid="newsletter-editor">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={closeEditor}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">
            {editingNewsletter ? 'Edit Newsletter' : 'Create Newsletter'}
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardContent className="p-6">
                <label className="text-sm font-medium mb-2 block">Newsletter Title</label>
                <Input
                  placeholder="e.g., April Newsletter, Weekly Update..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-lg"
                  data-testid="newsletter-title"
                />
              </CardContent>
            </Card>

            {/* Sections */}
            <div className="space-y-4">
              {sections.map((section, index) => {
                const sType = getSectionType(section.type);
                const IconComp = sType.icon;
                return (
                  <Card key={section.id} className="overflow-hidden border-0 shadow-md">
                    <div className={`flex items-center justify-between px-4 py-3 ${sType.color} border-b`}>
                      <div className="flex items-center gap-3">
                        <IconComp className="w-5 h-5" />
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                          className="bg-transparent border-none text-inherit font-semibold focus-visible:ring-0 w-auto h-auto p-0"
                          style={{ width: `${Math.max(section.title.length, 10)}ch` }}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => moveSection(index, 'up')} disabled={index === 0} className="h-8 w-8 p-0 hover:bg-white/50">
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="h-8 w-8 p-0 hover:bg-white/50">
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeSection(section.id)} className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <Textarea
                        placeholder={`Write your ${sType.label.toLowerCase()} here...`}
                        value={section.content}
                        onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                        onFocus={() => setActiveSectionId(section.id)}
                        rows={5}
                        className="border-0 focus-visible:ring-0 resize-none"
                      />
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => handleAiAssist('suggest', section.id)} disabled={aiLoading} className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                          <Sparkles className="w-4 h-4 mr-1" />
                          AI Generate
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleAiAssist('improve', section.id)} disabled={aiLoading || !section.content} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                          Improve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full border-dashed border-2 hover:border-primary hover:bg-primary/5">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Section
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {sectionTypes.map((st) => {
                    const Icon = st.icon;
                    return (
                      <DropdownMenuItem key={st.type} onClick={() => addSection(st.type)}>
                        <Icon className="w-4 h-4 mr-2" />
                        {st.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Images & Attachments */}
            <Card>
              <CardContent className="p-4">
                {images.length > 0 && (
                  <div className="mb-4">
                    <label className="text-sm font-medium mb-2 block">Images</label>
                    <div className="flex flex-wrap gap-2">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={`${API}${img}`} alt="" className="w-20 h-20 object-cover rounded" />
                          <button onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {attachments.length > 0 && (
                  <div className="mb-4">
                    <label className="text-sm font-medium mb-2 block">Attachments</label>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-muted px-3 py-1 rounded">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{att.name}</span>
                          <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    <Button type="button" variant="outline" size="sm" asChild><span><Image className="w-4 h-4 mr-2" />Add Image</span></Button>
                  </label>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleAttachmentUpload} />
                    <Button type="button" variant="outline" size="sm" asChild><span><Paperclip className="w-4 h-4 mr-2" />Add Attachment</span></Button>
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Audience</label>
                  <Select value={audienceType} onValueChange={setAudienceType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="center"><div className="flex items-center gap-2"><Users className="w-4 h-4" />All Parents</div></SelectItem>
                      <SelectItem value="classroom"><div className="flex items-center gap-2"><School className="w-4 h-4" />Specific Classroom</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {audienceType === 'classroom' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Classroom</label>
                    <Select value={classroomId} onValueChange={setClassroomId}>
                      <SelectTrigger><SelectValue placeholder="Select classroom..." /></SelectTrigger>
                      <SelectContent>
                        {classrooms.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium mb-2 block">Frequency</label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  AI Assistant
                </CardTitle>
                <CardDescription>Enter a topic to generate content</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input placeholder="e.g., Spring activities, safety reminders..." value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-prompt" />
                <Button className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600" onClick={() => handleAiAssist('suggest')} disabled={aiLoading || !aiPrompt.trim()}>
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate Content
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <Button variant="outline" className="w-full" onClick={() => {
                  setPreviewNewsletter({ title, content: sectionsToContent(), audience_type: audienceType, classroom_name: classrooms.find(c => c.id === classroomId)?.name, images, attachments });
                  setShowPreview(true);
                }} disabled={!title || !sections.some(s => s.content.trim())} data-testid="preview-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Brochure
                </Button>
                <Button className="w-full" onClick={saveNewsletter} disabled={saving} data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
                {editingNewsletter && editingNewsletter.status === 'draft' && (
                  <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => { saveNewsletter(); publishNewsletter(editingNewsletter.id); }} disabled={publishing || saving}>
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Publish & Send
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ====== LIST VIEW ======
  return (
    <div className="space-y-8" data-testid="newsletters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" onClick={() => navigate(-1)} data-testid="newsletter-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-primary" />
            Newsletters
          </h1>
          <p className="text-muted-foreground mt-1">Create beautiful brochure-style newsletters for parents</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowPosterDialog(true)} data-testid="poster-newsletter-btn" className="bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100">
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Poster Newsletter
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-newsletter-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </Button>
          <Button variant="outline" onClick={() => setShowGenerateDialog(true)} disabled={generatingFull} data-testid="ai-generate-full-btn">
            {generatingFull ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            AI Generate
          </Button>
          <Button onClick={() => openEditor()} data-testid="create-newsletter-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Newsletter
          </Button>
        </div>
      </div>

      {/* Generation Progress */}
      {generatingFull && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-6 text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
            <p className="font-medium text-primary">Generating your newsletter...</p>
            <p className="text-sm text-muted-foreground mt-1">AI is writing all sections for you</p>
          </CardContent>
        </Card>
      )}

      {/* Newsletter List */}
      {newsletters.length === 0 ? (
        <Card className="p-16 text-center border-dashed border-2">
          <Newspaper className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="font-semibold text-xl mb-2">No newsletters yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Create your first brochure-style newsletter to keep parents engaged
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button variant="outline" onClick={() => setShowPosterDialog(true)} className="bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100">
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Poster Newsletter
            </Button>
            <Button variant="outline" onClick={() => setShowImportDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Existing
            </Button>
            <Button variant="outline" onClick={() => setShowGenerateDialog(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              AI Generate
            </Button>
            <Button onClick={() => openEditor()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Manually
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsletters.map((newsletter) => (
            <Card key={newsletter.id} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-0 shadow-md">
              <div className={`h-2 ${newsletter.status === 'published' ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`} />
              <CardHeader className="pb-3">
                <div className="space-y-2">
                  <CardTitle className="text-xl font-bold line-clamp-1 group-hover:text-primary transition-colors">
                    {newsletter.title}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${newsletter.status === 'published' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'} border`}>
                      {newsletter.status === 'published' ? <><CheckCircle className="w-3 h-3 mr-1" /> Published</> : <><Edit className="w-3 h-3 mr-1" /> Draft</>}
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {newsletter.audience_type === 'center' ? <><Users className="w-3 h-3 mr-1" /> All Parents</> : <><School className="w-3 h-3 mr-1" /> {newsletter.classroom_name}</>}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground line-clamp-3 mb-4 leading-relaxed text-sm">{newsletter.content?.substring(0, 150)}...</p>
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-4 py-3 border-t border-b">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    {new Date(newsletter.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  {newsletter.email_sent && (
                    <span className="flex items-center gap-2 text-green-600 font-medium">
                      <Mail className="w-4 h-4" />{newsletter.email_sent_count} sent
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => { setPreviewNewsletter(newsletter); setShowPreview(true); }} data-testid={`view-${newsletter.id}`}>
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  {newsletter.status === 'draft' && (
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditor(newsletter)} data-testid={`edit-${newsletter.id}`}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleExportPDF(newsletter.id)} data-testid={`download-${newsletter.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadShareCard(newsletter)} data-testid={`share-card-${newsletter.id}`} title="Download social share card">
                    <Share2 className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopyShareCaption(newsletter)} data-testid={`share-caption-${newsletter.id}`} title="Copy AI-written social caption">
                    <ClipboardCopy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDuplicateNewsletter(newsletter)} data-testid={`duplicate-${newsletter.id}`} title="Duplicate as new draft">
                    <Copy className="w-4 h-4" />
                  </Button>
                  {newsletter.status === 'draft' && (
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => publishNewsletter(newsletter.id)} disabled={publishing} data-testid={`publish-${newsletter.id}`}>
                      <Send className="w-4 h-4 mr-1" />
                      Publish
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deleteNewsletter(newsletter.id)} data-testid={`delete-${newsletter.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Brochure Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">
          <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between">
            <DialogTitle className="text-lg font-bold">Newsletter Preview</DialogTitle>
            <div className="flex gap-2">
              {previewNewsletter?.id && (
                <Button size="sm" variant="outline" onClick={() => handleExportPDF(previewNewsletter.id)} data-testid="preview-download-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
            </div>
          </div>
          <div className="p-6 bg-gray-100">
            <BrochurePreview newsletter={previewNewsletter} />
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Full Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI Generate Newsletter
            </DialogTitle>
            <DialogDescription>
              Generate a complete brochure-style newsletter with AI
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Period/Title</label>
              <Input value={generatePeriod} onChange={(e) => setGeneratePeriod(e.target.value)} placeholder="e.g., April 2026, Spring Update..." data-testid="generate-period" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Special Notes (Optional)</label>
              <Textarea value={generateNotes} onChange={(e) => setGenerateNotes(e.target.value)} placeholder="Any specific topics, events, or themes to include..." rows={3} data-testid="generate-notes" />
            </div>
            <Card className="bg-muted/30">
              <CardContent className="py-3">
                <h4 className="text-sm font-medium mb-2">Sections that will be generated:</h4>
                <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                  {sectionTypes.slice(0, -1).map(st => {
                    const Icon = st.icon;
                    return (
                      <div key={st.type} className="flex items-center gap-1.5">
                        <Icon className="w-3 h-3" />
                        {st.label}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateFullNewsletter} disabled={generatingFull || !generatePeriod.trim()} data-testid="generate-newsletter-submit">
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Newsletter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload / Import Newsletter Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) setImportFile(null); setShowImportDialog(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Upload Existing Newsletter
            </DialogTitle>
            <DialogDescription>
              Import a newsletter you&apos;ve already written and edit it here — manually or with AI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Newsletter File</label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="newsletter-import-file"
                  data-testid="import-file-input"
                />
                <label htmlFor="newsletter-import-file" className="cursor-pointer block">
                  {importFile ? (
                    <>
                      <FileText className="w-10 h-10 text-primary mx-auto mb-2" />
                      <p className="font-medium text-sm">{importFile.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(importFile.size / 1024).toFixed(1)} KB — click to change
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground/60 mx-auto mb-2" />
                      <p className="text-sm font-medium">Click to choose a file</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, or TXT</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-purple-50/40">
              <input
                type="checkbox"
                checked={importRefineWithAi}
                onChange={(e) => setImportRefineWithAi(e.target.checked)}
                className="mt-0.5"
                data-testid="import-refine-ai-toggle"
              />
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Refine with AI on import
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  AI will keep your facts and dates but reorganize the copy into brochure sections and tighten the tone.
                  Uncheck to import the raw text as one editable section.
                </p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportFile(null); }} disabled={importing}>
              Cancel
            </Button>
            <Button
              onClick={handleImportNewsletter}
              disabled={importing || !importFile}
              data-testid="import-newsletter-submit"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {importRefineWithAi ? 'Import & Refine' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Poster Newsletter builder */}
      <PosterBuilderDialog open={showPosterDialog} onOpenChange={setShowPosterDialog} />
    </div>
  );
};

export default NewslettersPage;
